import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SectionList from '../components/ui/sections-list';
import { motion } from 'framer-motion';

const typingVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            delay: 0.3,
            staggerChildren: 0.04,
        },
    },
};

const letterVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
};

const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.3,
            ease: "easeOut",
            when: "beforeChildren",
            staggerChildren: 0.4,
            delay: 0.4,
        },
    },
};

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5 }
    }
};

const FurnitureProPage = () => {
    const [furniture, setFurniture] = useState([]);
    const [filteredFurniture, setFilteredFurniture] = useState([]);
    const [activeFilter, setActiveFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    const filterCategories = [
        { id: 'all', name: 'Tous' },
        { id: 'Bureau', name: 'Bureaux' },
        { id: 'Siège', name: 'Sièges' },
        { id: 'Réunion', name: 'Réunion' },
        { id: 'Rangement', name: 'Rangement' },
        { id: 'Espace d\'accueil', name: 'Espaces d\'accueil' },
        { id: 'Concentration', name: 'Concentration' },
    ];

    const filterItems = (category) => {
        setActiveFilter(category);
        if (category === 'all') {
            setFilteredFurniture(furniture);
        } else {
            const filtered = furniture.filter(item => item.category === category);
            setFilteredFurniture(filtered);
        }
    };

    const handleSearch = (event) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);

        let results = activeFilter === 'all'
            ? furniture
            : furniture.filter(item => item.category === activeFilter);

        if (term) {
            results = results.filter(item =>{
                const title = item.title.toLowerCase() || "";
                return title.includes(term);
            }
            );
        }

        setFilteredFurniture(results);
    };

    useEffect(() => {
        const fetchFurniture = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/furnitures-pro?populate=*`, {
                    headers: {
                        Authorization: `${process.env.REACT_APP_API_TOKEN}`
                    }
                });
                setFurniture(response.data.data);
                setFilteredFurniture(response.data.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching furniture data:', error);
                setLoading(false);
            }
        };

        fetchFurniture();
    }, []);

    if (loading) return <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
    </div>;

    return (
        <div className='bg-cocoa'>
            {/* Landing Page */}
            <section id="landing-page" className="relative min-h-[100vh] w-full overflow-hidden">
                <div className="flex flex-col lg:flex-row h-full w-full">
                    {/* Image à gauche */}
                    <div className="w-full lg:w-2/3 h-[50vh] lg:h-screen relative">
                        <img
                            alt="Mobilier professionnel"
                            src="/assets/images/mobilier-pro/CATEGORIE PROFESSIONNEL.jpg"
                            className="w-full h-full object-cover lg:rounded-l-3xl"
                        />
                    </div>

                    {/* Contenu à droite */}
                    <div className="w-full lg:w-1/2 h-[50vh] lg:h-screen">
                        <div className="bg-cream h-full p-4 sm:p-6 md:p-8 lg:p-12 flex flex-col justify-between lg:rounded-r-3xl">
                            <motion.h2
                                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-7xl font-light font-cormorant text-cocoa text-center uppercase"
                                variants={typingVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {"Mobilier professionnel".split("").map((char, index) => (
                                    <motion.span key={index} variants={letterVariants}>
                                        {char}
                                    </motion.span>
                                ))}
                            </motion.h2>
                            <div className="w-full h-px bg-cocoa my-4 lg:my-6"></div>
                            <motion.img
                                alt="Mobilier professionnel"
                                src="/assets/images/mobilier-pro/SOUS CATEGORIE PROFESSIONNEL.jpg"
                                className="object-cover w-full h-1/3 md:h-1/2 rounded-lg"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1 }}
                            />
                            <motion.p
                                className="text-cocoa text-center text-sm sm:text-base md:text-lg lg:text-xl mt-4"
                                variants={typingVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {"Découvrez notre gamme de mobilier professionel pour équiper vos bureaux, salles de réunion, espaces d'accueil et de rangement.".split("").map((char, index) => (
                                    <motion.span key={index} variants={letterVariants}>
                                        {char}
                                    </motion.span>
                                ))}
                            </motion.p>
                            <div className="absolute bottom-4 lg:bottom-10 left-1/2 transform -translate-x-1/2 text-white text-center">
                                <p className="text-base lg:text-lg">Scroll Down</p>
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 mx-auto mt-2 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section des filtres */}
            <motion.section
                className='min-h-screen pb-10 md:pb-20 px-4'
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={sectionVariants}
            >
                <motion.h2
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-7xl font-light font-cormorant text-cream text-center mt-6 lg:mt-10"
                    variants={typingVariants}
                    initial="hidden"
                    animate="visible"
                >
                    Nos Produits
                </motion.h2>
                <div className="w-auto h-px bg-cream my-4 sm:my-6 lg:my-8 mx-4 sm:mx-8 lg:mx-10"></div>

                {/* Filtres */}
                <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 mb-6 sm:mb-8 px-2 sm:px-4">
                    {filterCategories.map(category => (
                        <motion.button
                            key={category.id}
                            onClick={() => filterItems(category.id)}
                            className={`px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base rounded-full transition-colors ${activeFilter === category.id
                                    ? 'bg-brandy-rose text-white'
                                    : 'bg-cream text-cocoa hover:bg-brandy-rose hover:text-white'
                                }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {category.name}
                        </motion.button>
                    ))}
                </div>
                <div className="w-full max-w-2xl mx-auto px-4 mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="relative"
                    >
                        <input
                            type="text"
                            placeholder="Rechercher un produit..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="w-full px-4 py-3 pl-12 rounded-full bg-cream text-cocoa placeholder-cocoa/50 focus:outline-none focus:ring-2 focus:ring-brandy-rose"
                        />
                        <svg
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-cocoa/50"
                            width="20"
                            height="20"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    </motion.div>
                </div>
                <div className='rounded-3xl bg-cream p-8 mx-4 sm:mx-8 md:p-12 lg:px-16 lg:pt-10 lg:pb-6'>
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="space-y-4"
                    >
                        {filteredFurniture.map((item, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                className="card"
                            >
                                <SectionList
                                    id={item.id}
                                    title={item.title}
                                    description={item.content?.[0]?.children?.[0]?.text || ""}
                                    images={item.imageArticle}
                                    additionalImages={item.additionalImages}
                                    author={item.author}
                                    category={item.category}
                                    documentId={`/categories/mobilier/professionnel/${item.documentId}`}
                                />
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>
        </div>
    );
};

export default FurnitureProPage;